import { Base64 } from "global-functions";

class StorageManager {
  constructor() {
    this.encode = true;
    this.dbName = `${window.location.hostname}_GreatEdge_DB_v1`;
    this.storeName = "Cache";
    this.dbInstance = null;

    this.initialize();
  }

  async initIndexedDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: "key" });
        }
      };

      request.onsuccess = () => {
        this.dbInstance = request.result;
        resolve();
      };

      request.onerror = () => {
        console.error("Error initializing IndexedDB:", request.error);
        reject(request.error);
      };
    });
  }

  async setIndexedDB(key, value) {
    return new Promise(async (resolve, reject) => {
      if (!this.dbInstance) {
        await this.initIndexedDB();
      }

      const transaction = this.dbInstance.transaction(
        this.storeName,
        "readwrite"
      );
      const store = transaction.objectStore(this.storeName);
      const request = store.put({ key, value });

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async getIndexedDB(key) {
    return new Promise(async (resolve, reject) => {
      if (!this.dbInstance) {
        await this.initIndexedDB();
      }

      const transaction = this.dbInstance.transaction(
        this.storeName,
        "readonly"
      );
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onsuccess = () =>
        resolve(request.result ? request.result.value : undefined);
      request.onerror = () => reject(request.error);
    });
  }

  async removeIndexedDB(key) {
    return new Promise(async (resolve, reject) => {
      if (!this.dbInstance) {
        await this.initIndexedDB();
      }

      const transaction = this.dbInstance.transaction(
        this.storeName,
        "readwrite"
      );
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve(key);
      request.onerror = () => reject(request.error);
    });
  }

  async set(key, value, useIndexedDB) {
    try {
      const data = this.encode
        ? this.encodeData(JSON.stringify(value))
        : JSON.stringify(value);

      if (!useIndexedDB) {
        window.localStorage.setItem(key, data);
      } else {
        await this.setIndexedDB(key, data);
      }

      return true;
    } catch (err) {
      console.error(`Error saving to storage.`, err);
      return false;
    }
  }

  async get(key, decode, retry) {
    try {
      // Check localStorage first
      let data = window.localStorage.getItem(key);
      if (data) {
        return decode || this.encode
          ? JSON.parse(this.decodeData(data))
          : JSON.parse(data);
      }

      // Fallback to IndexedDB
      data = await this.getIndexedDB(key);
      return data
        ? decode || this.encode
          ? JSON.parse(this.decodeData(data))
          : JSON.parse(data)
        : undefined;
    } catch (err) {
      if (!retry) {
        return await this.get(key, true, true);
      }

      return undefined;
    }
  }

  getLocalStorageItem(key, decode) {
    try {
      // Check localStorage first
      let data = window.localStorage.getItem(key);
      return decode || this.encode
        ? JSON.parse(this.decodeData(data))
        : JSON.parse(data);
    } catch (err) {
      return undefined;
    }
  }

  async remove(key) {
    try {
      // Remove from localStorage
      window.localStorage.removeItem(key);

      // Remove from IndexedDB
      await this.removeIndexedDB(key);

      return key;
    } catch (err) {
      console.error(`Error removing from storage.`, err);
      return null;
    }
  }

  encodeData(dataStr) {
    return Base64.encode(dataStr);
  }

  decodeData(dataStr) {
    return Base64.decode(dataStr);
  }

  async initialize({ encode = false } = {}) {
    this.encode = encode;
    if (!this.dbInstance) {
      await this.initIndexedDB();
    }
  }
}

const instance = new StorageManager();
export default instance;
